// More: the frame's rows, then what the app needs and the frame does not draw.
//
// Frame v2.12 template L393-401, script L910 `moreRowsList`: one card of
// rows — Pick work centre, Who worked a pod today, Standard work library,
// Buffer boards — then a version footer. The raise kinds are not here: the
// floating + holds them on a phone and the foot of the rail on a tablet, so
// a card for each on More would be a third door to the same three things.
//
// Below a divider, the app's own rows: My work (the to-dos and quality
// issues the Quality tab no longer lists), and Notifications, Language, Sign
// out. The frame's "Switch role" row is not drawn: the role comes from the
// Odoo session and this session can never switch it, so there is no case in
// which the row would do anything.

import { Icon, type IconName } from '@/components/ui/Icon';
import { StatChip } from '@/components/home/Score';
import { useT } from '@/i18n/useT';
import { useCrewToday, useIswRegister, useMyAlerts, useTodos } from '@/queries';
import { useStore } from '@/store';

export function MoreScreen({
  onOpenSettings,
  onPickStation,
  onOpenAlerts,
  onOpenNotifications,
  onOpenBufferBoards,
  onOpenLibrary,
  onOpenCrew,
  onSignOut,
}: {
  onOpenSettings: () => void;
  onPickStation: () => void;
  onOpenAlerts: () => void;
  /**
   * The notifications sheet. Absent when the session has nothing to show in
   * it — pushes are the andon module's — and the row goes with it, because a
   * row that opens nothing is a dead tap.
   */
  onOpenNotifications?: () => void;
  onOpenBufferBoards: () => void;
  onOpenLibrary: () => void;
  onOpenCrew: () => void;
  onSignOut: () => void;
}) {
  const t = useT();
  const wcName = useStore((s) => s.workcentreName);
  const flName = useStore((s) => s.flowlineName);
  const language = useStore((s) => s.language);
  const iswAvailable = useStore((s) => s.iswAvailable);
  // Said in the footer, so a session can be told apart from the one next to
  // it when the thread opens here on one phone and in the Andon app on the
  // other.
  const andonPreview = useStore((s) => s.andonCaps.preview);

  const todos = useTodos();
  const myAlerts = useMyAlerts();
  const crew = useCrewToday();
  // Only to count the row's pill — the register is small and cached, and the
  // screen behind the row reads the same query.
  const isw = useIswRegister(iswAvailable);

  const station = [wcName, flName].filter(Boolean).join(' · ') || t.noStationSetT;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h1 style={{ font: "700 18px 'Space Grotesk',sans-serif", margin: 0 }}>{t.moreT}</h1>
      {/* The frame's rows, in its order. */}
      <FrameRows
        rows={[
          { label: t.morePickWorkCentreT, sub: station, onClick: onPickStation },
          {
            label: t.moreWhoWorkedT,
            sub: crew.data ? `${new Set(crew.data.map((c) => c.employeeId)).size} ${t.moreCrewSubT}` : '—',
            onClick: onOpenCrew,
          },
          // Only where a library is actually configured behind this deploy —
          // a row that can only 404 is worse than no row.
          ...(iswAvailable ? [{ label: t.moreLibraryT, sub: `${t.moreLibrarySubT}${isw.data ? ` · ${isw.data.rows.length}` : ''}`, onClick: onOpenLibrary }] : []),
          { label: t.bufferBoardsT, sub: t.moreBufferSubT, onClick: onOpenBufferBoards, external: true },
        ]}
      />
      <div style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)', lineHeight: 1.55, padding: '0 4px' }}>
        {t.moreVersionT} {__APP_VERSION__}
        {andonPreview ? ` · ${t.moreAndonPreviewT}` : ''}
      </div>

      <hr aria-hidden="true" style={{ border: 0, borderTop: '1px solid var(--line)', margin: '4px 0' }} />

      <Group label={t.myWorkT}>
        <Row icon="list" label={t.myTodosT} count={todos.data?.length ?? 0} onClick={onOpenAlerts} />
        <Row icon="check" label={t.myQualityIssuesT} count={myAlerts.data?.length ?? 0} onClick={onOpenAlerts} />
        <Row icon="clock" label={t.myHistoryT} onClick={onOpenAlerts} last />
      </Group>

      <Group label={t.settings}>
        {onOpenNotifications && <Row icon="bell" label={t.notificationsT} onClick={onOpenNotifications} />}
        <Row icon="gear" label={`${t.language} · ${language}`} onClick={onOpenSettings} />
        <Row icon="logout" label={t.signOutDevice} onClick={onSignOut} last />
      </Group>
    </div>
  );
}

/** The frame's card of rows: label over a sub-line, a chevron, a rule between (template L395-399). */
function FrameRows({ rows }: { rows: Array<{ label: string; sub: string; onClick: () => void; external?: boolean }> }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: '4px 14px', display: 'flex', flexDirection: 'column' }}>
      {rows.map((m, i) => (
        <button
          key={m.label}
          type="button"
          className="press-row"
          onClick={m.onClick}
          style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 48, borderTop: i === 0 ? 'none' : '1px solid var(--surface2)', textAlign: 'left', whiteSpace: 'normal', width: '100%' }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', font: '700 13px Manrope,sans-serif' }}>{m.label}</span>
            <span style={{ display: 'block', font: '500 11px Manrope,sans-serif', color: 'var(--sub)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.sub}</span>
          </span>
          <span aria-hidden="true" style={{ color: 'var(--sub)', display: 'flex' }}>
            <Icon name={m.external ? 'external' : 'chevron'} size={14} />
          </span>
        </button>
      ))}
    </div>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section aria-label={label}>
      <div className="sectionLabel">{label}</div>
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 18,
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
    </section>
  );
}

function Row({
  icon,
  label,
  count,
  onClick,
  last = false,
}: {
  icon: IconName;
  label: string;
  count?: number;
  onClick: () => void;
  last?: boolean;
}) {
  return (
    <button
      type="button"
      className="press-row"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        textAlign: 'left',
        padding: '14px 16px',
        minHeight: 44,
        borderBottom: last ? 'none' : '1px solid var(--surface2)',
      }}
    >
      <span style={{ color: 'var(--accent)', display: 'flex' }}>
        <Icon name={icon} size={19} strokeWidth={2} />
      </span>
      <span style={{ flex: 1, minWidth: 0, font: '600 13.5px Manrope,sans-serif' }}>{label}</span>
      {count != null && count > 0 && (
        <StatChip tone="neutral" size="sm">
          {count}
        </StatChip>
      )}
      <span aria-hidden="true" style={{ color: 'var(--sub)', display: 'flex' }}>
        <Icon name="chevron" size={14} />
      </span>
    </button>
  );
}
